import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { isPublicPurchaseMotion } from 'purchase-motions-components/utils/purchaseMotionTypeGuards';
import { lazy, Suspense } from 'react';
import DisabledDowngradeButton from '../components/purchase-motions/DisabledDowngradeButton';
import RedirectToCustomerPlatformButton from '../components/purchase-motions/RedirectToCustomerPlatformButton';
import RedirectToMarketingPlusButton from '../components/purchase-motions/RedirectToMarketingPlusButton';
import { SingleSkeletonLoader } from '../components/SingleSkeletonLoader';
import { usePurchaseMotionOverrides } from '../hooks/purchase-motions/usePurchaseMotionOverrides';
import { usePurchaseMotionsUniversal } from '../hooks/purchase-motions/usePurchaseMotionsUniversal';
import { useIsInAppAuth } from '../hooks/user/useUserInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PurchaseMotionCTAWrapper = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "PurchaseMotionCTAWrapper" */'./PurchaseMotionCTAWrapper').then(module => ({
  default: module.PurchaseMotionCTAWrapper
})));
const PublicPurchaseMotionCTAWrapper = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "PublicPurchaseMotionCTAWrapper" */'./PublicPurchaseMotionCTAWrapper').then(module => ({
  default: module.PublicPurchaseMotionCTAWrapper
})));
export const PurchaseMotionContainer = ({
  displayCount,
  location,
  productApiName,
  purchaseRequest,
  skipInterstitial,
  trackingData,
  size = 'default',
  disabled,
  purchaseMotions: purchaseMotionsFromProps
}) => {
  const isInAppAuth = useIsInAppAuth();
  const {
    loading: overrideLoading,
    type: overrideType
  } = usePurchaseMotionOverrides(productApiName);
  const {
    purchaseMotions: purchaseMotionsFromQuery,
    loading: purchaseMotionsLoading
  } = usePurchaseMotionsUniversal({
    productApiName,
    location,
    isInAppAuth,
    skip: overrideLoading || !productApiName || !location || !!purchaseMotionsFromProps
  });
  const purchaseMotions = purchaseMotionsFromProps || purchaseMotionsFromQuery;
  if (overrideLoading || purchaseMotionsLoading) {
    if (displayCount) {
      return /*#__PURE__*/_jsx(_Fragment, {
        children: Array.from({
          length: displayCount
        }, (__, index) => /*#__PURE__*/_jsx(SingleSkeletonLoader, {
          height: 40
        }, `purchase-motion-loader-${index}-${productApiName}-${location}`))
      });
    }
    return /*#__PURE__*/_jsx(SingleSkeletonLoader, {
      height: 40
    });
  }
  if (overrideType === 'DISABLED_DOWNGRADE') {
    return /*#__PURE__*/_jsx(DisabledDowngradeButton, {
      size: size
    });
  }
  if (overrideType === 'REDIRECT_MARKETING_PLUS') {
    return /*#__PURE__*/_jsx(RedirectToMarketingPlusButton, {
      trackingSource: trackingData.source,
      size: size
    });
  }
  if (overrideType === 'REDIRECT_CUSTOMER_PLATFORM') {
    return /*#__PURE__*/_jsx(RedirectToCustomerPlatformButton, {
      trackingSource: trackingData.source,
      "data-selenium-id": trackingData.source,
      size: size
    });
  }

  /**
   * Public Purchase Motion CTAs
   */
  if (!isInAppAuth) {
    const publicPurchaseMotions = purchaseMotions === null || purchaseMotions === void 0 ? void 0 : purchaseMotions.filter(isPublicPurchaseMotion);
    if (!publicPurchaseMotions || publicPurchaseMotions.length === 0) {
      return /*#__PURE__*/_jsx(View, {
        blockSize: "40px",
        inlineSize: "100%"
      });
    }
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      justifyContent: "center",
      gap: "200",
      inlineSize: "100%",
      children: Array.from({
        length: displayCount || publicPurchaseMotions.length
      }, (__, displayIndex) => {
        const purchaseMotion = publicPurchaseMotions[displayIndex];
        if (!purchaseMotion) {
          return null;
        }
        return /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(SingleSkeletonLoader, {
              height: 40
            }),
            children: /*#__PURE__*/_jsx(PublicPurchaseMotionCTAWrapper, {
              purchaseMotion: purchaseMotion,
              trackingData: trackingData,
              productApiName: productApiName,
              location: location,
              displayIndex: displayIndex,
              disabled: disabled
            })
          })
        }, `public-purchase-motion-${purchaseMotion.toString()}`);
      })
    });
  }
  if (!purchaseMotions || purchaseMotions.length === 0) {
    return /*#__PURE__*/_jsx(View, {
      blockSize: "40px",
      inlineSize: "100%"
    });
  }

  /**
   * In-app Purchase Motion CTAs
   */
  return /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    justifyContent: "center",
    gap: "200",
    inlineSize: "100%",
    children: Array.from({
      length: displayCount || purchaseMotions.length
    }, (__, displayIndex) => {
      const purchaseMotion = purchaseMotions[displayIndex];
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(SingleSkeletonLoader, {
            height: 40
          }),
          children: /*#__PURE__*/_jsx(PurchaseMotionCTAWrapper, {
            purchaseMotion: purchaseMotion,
            trackingData: trackingData,
            productApiName: productApiName,
            location: location,
            displayIndex: displayIndex,
            purchaseRequest: purchaseRequest,
            skipInterstitial: skipInterstitial,
            disabled: disabled
          })
        })
      }, `purchase-motion-${purchaseMotion.toString()}`);
    })
  });
};